// Konitys Permissions — React bindings for the global konitys-perms SDK.
//
// The SDK (served by the gateway) exposes window.__KONITYS_PERMS. We load it
// dynamically once the Keycloak token is available, then expose React helpers:
//
//   - loadPermsSDK({ token, gatewayUrl })   install the script tag
//   - usePerm(key)                          reactive boolean
//   - <Gate perm="contacts.create">         conditional render
//   - <PageGuard perm="pages.contacts.view"> redirect-to-403 if denied
//
// All helpers default to "allowed" while the SDK is loading so the UI does
// not flicker empty on first paint.

import React, { useEffect, useState, cloneElement, isValidElement } from 'react';
import { Navigate } from 'react-router-dom';

const APP_KEY = 'antennes';
let _sdkLoaded = false;

export function loadPermsSDK({ token, gatewayUrl }) {
  if (!token || !gatewayUrl) return;
  if (_sdkLoaded && window.__KONITYS_PERMS) {
    window.__KONITYS_PERMS.configure({ token });
    window.__KONITYS_PERMS.refresh();
    return;
  }
  _sdkLoaded = true;
  window.__KONITYS_AUTH = { token };
  const s = document.createElement('script');
  s.src = `${gatewayUrl.replace(/\/$/, '')}/sdk/konitys-perms.js?t=${Date.now()}`;
  s.setAttribute('data-gateway', gatewayUrl);
  s.setAttribute('data-token', token);
  s.setAttribute('data-app', APP_KEY);
  s.async = true;
  document.body.appendChild(s);
}

function getSDK() { return typeof window !== 'undefined' ? window.__KONITYS_PERMS : null; }

export function usePerm(permKey) {
  const [sdk, setSdk] = useState(getSDK());
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!sdk) {
      const iv = setInterval(() => {
        const cur = getSDK();
        if (cur) { clearInterval(iv); setSdk(cur); }
      }, 100);
      return () => clearInterval(iv);
    }
    const off = sdk.onChange(() => setTick((t) => t + 1));
    return off;
  }, [sdk]);
  // Optimistic default while loading → avoid flicker
  if (!sdk || !permKey) return true;
  return sdk.has(permKey);
}

export function Gate({ perm, children, fallback = null }) {
  const allowed = usePerm(perm);
  if (!allowed) return fallback;
  // Clone the single child and tag with data-perm so audit-permissions.sh
  // can verify coverage. Accept multiple children too (returned as-is).
  if (isValidElement(children)) {
    const existing = children.props['data-perm'];
    return cloneElement(children, { 'data-perm': existing || `${APP_KEY}:${perm}` });
  }
  return children;
}

export function PageGuard({ perm, children }) {
  const allowed = usePerm(perm);
  if (!allowed) return <Navigate to="/403" replace />;
  return children;
}

export { APP_KEY };
